import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Search } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import ProductCard from '@/components/ProductCard';
import { searchScore, compareSearchResults } from '@/lib/productSearch';

export default function CollectionPreview() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchVal, setSearchVal] = useState('');
  const [selectedSizes, setSelectedSizes] = useState([]);
  const navigate = useNavigate();

  const submitSearch = (e) => {
    e.preventDefault();
    const q = searchVal.trim();
    if (!q) return;
    navigate(`/safnid?q=${encodeURIComponent(q)}`);
  };

  useEffect(() => {
    base44.entities.Product.filter(
      { published: true },
      { sort: '-created_date', limit: 500, fields: ['name', 'team', 'season', 'category', 'deild', 'condition', 'size', 'price', 'images', 'status', 'reserved_at', 'featured', 'description', 'story'] }
    )
      .then((page) => {
        setProducts(page.items || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = searchVal.trim().toLocaleLowerCase('is-IS');
    const sized = selectedSizes.length ? products.filter(p => selectedSizes.includes(p.size)) : products;
    if (!q) return sized.slice(0, 6);
    return sized.filter((p) => searchScore(p, q) >= 0).sort((a, b) => compareSearchResults(a, b, q));
  }, [products, searchVal, selectedSizes]);

  return (
    <section id="urval" className="bg-card/50 border-y border-border">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="mb-6 border-b border-border pb-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-accent mb-2">Úrvalið</p>
              <Link to="/safnid" className="inline-block">
                <h2 className="font-heading text-4xl md:text-6xl text-primary hover:text-accent transition-colors">Nýjustu treyjurnar</h2>
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <p className="hidden md:block font-mono text-xs text-muted-foreground">Uppfært reglulega</p>
              <form onSubmit={submitSearch} className="relative w-full md:w-auto">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                  type="search"
                  value={searchVal}
                  onChange={(e) => setSearchVal(e.target.value)}
                  placeholder="Leita að treyjum..."
                  className="w-full md:w-56 pl-9 pr-3 py-2.5 bg-background border border-border text-sm font-body text-primary placeholder:text-muted-foreground focus:outline-none focus:border-accent rounded-sm"
                />
              </form>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-6" aria-label="Sía eftir stærðum">
          <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest mr-2">Stærð</span>
          {['S','M','L','XL','XXL'].map(size => (
            <button key={size} type="button" aria-pressed={selectedSizes.includes(size)}
              onClick={() => setSelectedSizes(prev => prev.includes(size) ? prev.filter(s => s !== size) : [...prev,size])}
              className={`min-w-11 py-2.5 px-3 border font-mono text-[11px] ${selectedSizes.includes(size) ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-primary hover:border-primary'}`}>
              {size}
            </button>
          ))}
          {selectedSizes.length > 0 && <button type="button" onClick={() => setSelectedSizes([])} className="font-mono text-[10px] underline text-accent ml-1">Hreinsa</button>}
        </div>
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-10">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-[4/5] bg-background animate-pulse" />
            ))}
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-10">
            {filtered.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="font-body text-sm text-muted-foreground">Engar treyjur fundust fyrir „{searchVal}“.</p>
            <Link to={`/safnid?q=${encodeURIComponent(searchVal.trim())}`} className="mt-3 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-accent underline">
              Leita í öllu úrvalinu <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        <div className="mt-8 md:mt-12 text-center">
          <Link
            to="/safnid"
            className="inline-flex items-center gap-2 border-2 border-primary text-primary px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            Skoðaðu allt úrvalið <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}